// ===================================
// TRENCH QUEST - Real Market Data Service
// Fetches live crypto market data from Jupiter (primary) and CoinGecko (fallback)
// ===================================

import type { RealMarketData, NewsItem } from '@/types'
import { fetchJupiterPrices } from './jupiterPriceService'

const COINGECKO_API_BASE = 'https://api.coingecko.com/api/v3'
const CACHE_DURATION_MS = 5 * 60 * 1000 // 5 minutes

// Jupiter price cache
let jupiterPriceCache: {
  data: Record<string, number>
  timestamp: number
} = {
  data: {},
  timestamp: 0,
}

// Cache for market data
let marketDataCache: {
  data: RealMarketData | null
  timestamp: number
} = {
  data: null,
  timestamp: 0,
}

// Cache for news
let newsCache: {
  data: NewsItem[] | null
  timestamp: number
} = {
  data: null,
  timestamp: 0,
}

// Solana ecosystem token IDs for CoinGecko (kept for future use)
// const SOLANA_TOKENS: Record<string, string> = {
//   SOL: 'solana', JUP: 'jupiter-exchange-solana', RAY: 'raydium',
//   BONK: 'bonk', WIF: 'dogwifcoin', PYTH: 'pyth-network',
//   JTO: 'jito-governance-token', ORCA: 'orca', RNDR: 'render-token', HNT: 'helium',
// }

// Map sentiment score to category
function getSentimentCategory(fearGreedIndex: number): RealMarketData['marketSentiment'] {
  if (fearGreedIndex <= 20) return 'extreme_fear'
  if (fearGreedIndex <= 40) return 'fear'
  if (fearGreedIndex <= 60) return 'neutral'
  if (fearGreedIndex <= 80) return 'greed'
  return 'extreme_greed'
}

/**
 * Fetch global market data including BTC, ETH, SOL prices and market cap
 */
async function fetchGlobalMarketData(): Promise<{
  btcPrice: number
  ethPrice: number
  solPrice: number
  totalMarketCap: number
  last24hVolume: number
  fearGreedIndex: number
}> {
  try {
    // Fetch prices for major tokens
    const priceResponse = await fetch(
      `${COINGECKO_API_BASE}/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd&include_24hr_change=true`
    )
    const priceData = await priceResponse.json()

    // Fetch global market data
    const globalResponse = await fetch(`${COINGECKO_API_BASE}/global`)
    const globalData = await globalResponse.json()

    // CoinGecko doesn't have fear/greed index - we'll estimate based on 24h change
    const btcChange = priceData.bitcoin?.usd_24h_change || 0
    const estimatedFearGreed = Math.min(100, Math.max(0, 50 + btcChange * 2))

    return {
      btcPrice: priceData.bitcoin?.usd || 0,
      ethPrice: priceData.ethereum?.usd || 0,
      solPrice: priceData.solana?.usd || 0,
      totalMarketCap: globalData.data?.total_market_cap?.usd || 0,
      last24hVolume: globalData.data?.total_volume?.usd || 0,
      fearGreedIndex: Math.round(estimatedFearGreed),
    }
  } catch (error) {
    console.error('Failed to fetch global market data:', error)
    // Return fallback values
    return {
      btcPrice: 95000,
      ethPrice: 3200,
      solPrice: 180,
      totalMarketCap: 3000000000000,
      last24hVolume: 100000000000,
      fearGreedIndex: 50,
    }
  }
}

/**
 * Fetch top gainers and losers from the market
 */
async function fetchTopMovers(): Promise<{
  gainers: RealMarketData['topGainers']
  losers: RealMarketData['topLosers']
}> {
  try {
    // Fetch Solana ecosystem tokens by market cap
    const response = await fetch(
      `${COINGECKO_API_BASE}/coins/markets?vs_currency=usd&category=solana-ecosystem&order=market_cap_desc&per_page=50&sparkline=false&price_change_percentage=24h`
    )
    const tokens = await response.json()

    if (!Array.isArray(tokens)) {
      throw new Error('Invalid response from CoinGecko')
    }

    // Sort by 24h change
    const sorted = [...tokens].sort(
      (a, b) => (b.price_change_percentage_24h || 0) - (a.price_change_percentage_24h || 0)
    )

    const gainers = sorted.slice(0, 5).map((t) => ({
      symbol: t.symbol?.toUpperCase() || 'UNKNOWN',
      name: t.name || 'Unknown',
      change24h: t.price_change_percentage_24h || 0,
      price: t.current_price || 0,
    }))

    const losers = sorted
      .slice(-5)
      .reverse()
      .map((t) => ({
        symbol: t.symbol?.toUpperCase() || 'UNKNOWN',
        name: t.name || 'Unknown',
        change24h: t.price_change_percentage_24h || 0,
        price: t.current_price || 0,
      }))

    return { gainers, losers }
  } catch (error) {
    console.error('Failed to fetch top movers:', error)
    // Return mock data
    return {
      gainers: [
        { symbol: 'BONK', name: 'Bonk', change24h: 15.5, price: 0.00002 },
        { symbol: 'WIF', name: 'dogwifhat', change24h: 12.3, price: 2.5 },
        { symbol: 'JUP', name: 'Jupiter', change24h: 8.7, price: 1.2 },
      ],
      losers: [
        { symbol: 'ORCA', name: 'Orca', change24h: -8.2, price: 4.5 },
        { symbol: 'RAY', name: 'Raydium', change24h: -5.1, price: 5.8 },
        { symbol: 'PYTH', name: 'Pyth Network', change24h: -3.4, price: 0.45 },
      ],
    }
  }
}

/**
 * Fetch trending tokens based on search popularity
 */
async function fetchTrendingTokens(): Promise<RealMarketData['trendingTokens']> {
  try {
    const response = await fetch(`${COINGECKO_API_BASE}/search/trending`)
    const data = await response.json()

    if (!data.coins || !Array.isArray(data.coins)) {
      throw new Error('Invalid trending response')
    }

    // Filter to Solana ecosystem tokens if possible, otherwise take top 5
    return data.coins.slice(0, 5).map((item: { item: { symbol: string; name: string; score: number } }) => ({
      symbol: item.item?.symbol?.toUpperCase() || 'UNKNOWN',
      name: item.item?.name || 'Unknown',
      mentions: item.item?.score || 0,
    }))
  } catch (error) {
    console.error('Failed to fetch trending tokens:', error)
    return [
      { symbol: 'SOL', name: 'Solana', mentions: 100 },
      { symbol: 'BONK', name: 'Bonk', mentions: 85 },
      { symbol: 'WIF', name: 'dogwifhat', mentions: 72 },
    ]
  }
}

/**
 * Get real market context - main export function
 * Caches results for 5 minutes to avoid rate limiting
 */
export async function getRealMarketContext(): Promise<RealMarketData> {
  const now = Date.now()

  // Return cached data if still valid
  if (marketDataCache.data && now - marketDataCache.timestamp < CACHE_DURATION_MS) {
    return marketDataCache.data
  }

  try {
    // Fetch all data in parallel
    const [globalData, movers, trending] = await Promise.all([
      fetchGlobalMarketData(),
      fetchTopMovers(),
      fetchTrendingTokens(),
    ])

    const marketData: RealMarketData = {
      ...globalData,
      topGainers: movers.gainers,
      topLosers: movers.losers,
      trendingTokens: trending,
      marketSentiment: getSentimentCategory(globalData.fearGreedIndex),
      lastUpdated: new Date(),
    }

    // Update cache
    marketDataCache = {
      data: marketData,
      timestamp: now,
    }

    return marketData
  } catch (error) {
    console.error('Failed to fetch real market context:', error)

    // Return cached data even if expired, or fallback
    if (marketDataCache.data) {
      return marketDataCache.data
    }

    // Return complete fallback data
    return {
      topGainers: [
        { symbol: 'BONK', name: 'Bonk', change24h: 12.5, price: 0.00002 },
        { symbol: 'WIF', name: 'dogwifhat', change24h: 8.3, price: 2.5 },
      ],
      topLosers: [
        { symbol: 'ORCA', name: 'Orca', change24h: -5.2, price: 4.5 },
        { symbol: 'RAY', name: 'Raydium', change24h: -3.1, price: 5.8 },
      ],
      trendingTokens: [
        { symbol: 'SOL', name: 'Solana', mentions: 100 },
        { symbol: 'JUP', name: 'Jupiter', mentions: 85 },
      ],
      btcPrice: 95000,
      ethPrice: 3200,
      solPrice: 180,
      marketSentiment: 'neutral',
      fearGreedIndex: 50,
      totalMarketCap: 3000000000000,
      last24hVolume: 100000000000,
      lastUpdated: new Date(),
    }
  }
}

/**
 * Fetch crypto news from CryptoPanic or similar service
 * For MVP, we'll use mock data with option to integrate real API later
 */
export async function getCryptoNews(): Promise<NewsItem[]> {
  const now = Date.now()

  // Return cached news if still valid
  if (newsCache.data && now - newsCache.timestamp < CACHE_DURATION_MS) {
    return newsCache.data
  }

  // For MVP, generate contextual mock news based on market data
  // TODO: Integrate CryptoPanic API or similar
  try {
    const marketData = await getRealMarketContext()

    const mockNews: NewsItem[] = []

    // Generate news based on market conditions
    if (marketData.marketSentiment === 'extreme_greed' || marketData.marketSentiment === 'greed') {
      mockNews.push({
        id: `news-${Date.now()}-1`,
        headline: 'Institutional FOMO Reaches New Heights',
        summary:
          'Major funds increasing crypto allocations as market sentiment turns euphoric.',
        source: 'CryptoDaily',
        url: '#',
        sentiment: 'bullish',
        relatedTokens: ['BTC', 'ETH', 'SOL'],
        publishedAt: new Date(),
      })
    }

    if (marketData.marketSentiment === 'extreme_fear' || marketData.marketSentiment === 'fear') {
      mockNews.push({
        id: `news-${Date.now()}-2`,
        headline: 'Market Sees Red as Uncertainty Grows',
        summary: 'Traders cautious amid macro concerns and regulatory headlines.',
        source: 'BlockNews',
        url: '#',
        sentiment: 'bearish',
        relatedTokens: ['BTC', 'ETH'],
        publishedAt: new Date(),
      })
    }

    // Add news about top gainers
    const topGainer = marketData.topGainers[0]
    if (topGainer) {
      mockNews.push({
        id: `news-${Date.now()}-3`,
        headline: `${topGainer.name} (${topGainer.symbol}) Surges ${topGainer.change24h.toFixed(1)}%`,
        summary: `${topGainer.symbol} leads the pack as traders pile into the token.`,
        source: 'TokenWatch',
        url: '#',
        sentiment: 'bullish',
        relatedTokens: [topGainer.symbol],
        publishedAt: new Date(),
      })
    }

    // Add news about top losers
    const topLoser = marketData.topLosers[0]
    if (topLoser) {
      mockNews.push({
        id: `news-${Date.now()}-4`,
        headline: `${topLoser.name} (${topLoser.symbol}) Drops ${Math.abs(topLoser.change24h).toFixed(1)}%`,
        summary: `Selling pressure continues for ${topLoser.symbol} amid broader market moves.`,
        source: 'CryptoAlert',
        url: '#',
        sentiment: 'bearish',
        relatedTokens: [topLoser.symbol],
        publishedAt: new Date(),
      })
    }

    // Always add some Solana-specific news
    mockNews.push({
      id: `news-${Date.now()}-5`,
      headline: 'Solana DeFi TVL Continues Growth Trajectory',
      summary:
        'The Solana ecosystem sees increased activity as new protocols launch.',
      source: 'DefiLlama',
      url: '#',
      sentiment: 'bullish',
      relatedTokens: ['SOL', 'JUP', 'RAY'],
      publishedAt: new Date(),
    })

    // Update cache
    newsCache = {
      data: mockNews,
      timestamp: now,
    }

    return mockNews
  } catch (error) {
    console.error('Failed to fetch crypto news:', error)

    // Return cached news even if expired
    if (newsCache.data) {
      return newsCache.data
    }

    // Fallback news
    return [
      {
        id: 'fallback-1',
        headline: 'Crypto Markets Continue to Evolve',
        summary: 'The market remains active with various developments.',
        source: 'CryptoNews',
        url: '#',
        sentiment: 'neutral',
        relatedTokens: ['BTC', 'ETH', 'SOL'],
        publishedAt: new Date(),
      },
    ]
  }
}

/**
 * Check if a specific token in the player's portfolio has significant movement
 * Used to trigger market-based situations
 */
export async function checkPortfolioAlerts(
  heldTokens: string[]
): Promise<{ token: string; change: number; direction: 'up' | 'down' }[]> {
  try {
    const marketData = await getRealMarketContext()
    const alerts: { token: string; change: number; direction: 'up' | 'down' }[] = []

    // Check if any held tokens are in top gainers (>10%)
    for (const gainer of marketData.topGainers) {
      if (heldTokens.includes(gainer.symbol) && gainer.change24h > 10) {
        alerts.push({
          token: gainer.symbol,
          change: gainer.change24h,
          direction: 'up',
        })
      }
    }

    // Check if any held tokens are in top losers (<-10%)
    for (const loser of marketData.topLosers) {
      if (heldTokens.includes(loser.symbol) && loser.change24h < -10) {
        alerts.push({
          token: loser.symbol,
          change: loser.change24h,
          direction: 'down',
        })
      }
    }

    return alerts
  } catch (error) {
    console.error('Failed to check portfolio alerts:', error)
    return []
  }
}

/**
 * Clear all caches - useful for testing or forcing refresh
 */
export function clearMarketDataCache(): void {
  marketDataCache = { data: null, timestamp: 0 }
  newsCache = { data: null, timestamp: 0 }
  jupiterPriceCache = { data: {}, timestamp: 0 }
}

/**
 * Fetch token prices from Jupiter API (Solana-native, more accurate for game tokens)
 * Used for initializing game with real prices
 */
export async function fetchGameTokenPrices(
  symbols: string[]
): Promise<Record<string, number>> {
  const now = Date.now()

  // Check cache first
  if (now - jupiterPriceCache.timestamp < CACHE_DURATION_MS) {
    const cachedPrices: Record<string, number> = {}
    let allFound = true

    for (const symbol of symbols) {
      const cachedPrice = jupiterPriceCache.data[symbol]
      if (cachedPrice !== undefined) {
        cachedPrices[symbol] = cachedPrice
      } else {
        allFound = false
        break
      }
    }

    if (allFound) {
      return cachedPrices
    }
  }

  try {
    const jupiterResult = await fetchJupiterPrices(symbols)
    const prices: Record<string, number> = {}

    for (const [symbol, data] of Object.entries(jupiterResult)) {
      prices[symbol] = data.price
    }

    // Update cache
    jupiterPriceCache = {
      data: { ...jupiterPriceCache.data, ...prices },
      timestamp: now,
    }

    return prices
  } catch (error) {
    console.error('Failed to fetch Jupiter prices:', error)
    return {}
  }
}

/**
 * Get the price source used (for debugging/display)
 */
export function getPriceSource(): 'jupiter' | 'coingecko' | 'cache' | 'none' {
  const now = Date.now()

  if (jupiterPriceCache.timestamp > 0 && now - jupiterPriceCache.timestamp < CACHE_DURATION_MS) {
    return 'jupiter'
  }
  if (marketDataCache.timestamp > 0 && now - marketDataCache.timestamp < CACHE_DURATION_MS) {
    return 'coingecko'
  }
  if (jupiterPriceCache.timestamp > 0 || marketDataCache.timestamp > 0) {
    return 'cache'
  }
  return 'none'
}
